import { useEffect, useMemo, useState } from 'react';

import { fetchTestTabFilterMetadata } from '@api/fetchers/goal/test-tab';
import type {
  ApiTabId,
  GetFilterResponse
} from '@api/fetchers/goal/test-tab/types';
import {
  getFilterConfigForCategoryTab,
  parseFilterMetadata
} from '@cont/SeeAll/TestSeriesListing/testTab/parseFilterMetadata';
import {
  showFiltersForTab,
  requiresAuthForTestTab
} from '@cont/SeeAll/TestSeriesListing/testTab/constants';
import type {
  CategoryTabId,
  TestSeriesView
} from '@cont/SeeAll/TestSeriesListing/testTab/types';

interface UseTestTabFilterMetadataParams {
  goalUid?: string;
  apiTabId: ApiTabId | null;
  language?: number;
  view: TestSeriesView;
  categoryTabId: CategoryTabId;
  isLoggedIn?: boolean;
}

const useTestTabFilterMetadata = ({
  goalUid,
  apiTabId,
  language = 1,
  view,
  categoryTabId,
  isLoggedIn = false
}: UseTestTabFilterMetadataParams) => {
  const needsAuth = requiresAuthForTestTab(view, categoryTabId, apiTabId);

  const shouldFetch = Boolean(
    goalUid &&
      apiTabId &&
      showFiltersForTab(view, categoryTabId) &&
      (!needsAuth || isLoggedIn)
  );

  const [data, setData] = useState<GetFilterResponse | undefined>();
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!shouldFetch || !goalUid || !apiTabId) {
      setData(undefined);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchTestTabFilterMetadata({
          goalUid,
          apiTabId,
          language
        });

        if (!cancelled) {
          setData(response);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError);
          setData(undefined);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [apiTabId, goalUid, isLoggedIn, language, needsAuth, shouldFetch]);

  const parsed = useMemo(() => parseFilterMetadata(data), [data]);

  const filterConfig = useMemo(
    () =>
      getFilterConfigForCategoryTab(
        categoryTabId,
        parsed.filterConfig,
        isLoggedIn
      ),
    [categoryTabId, isLoggedIn, parsed.filterConfig]
  );

  return {
    filterConfig,
    isLoading: shouldFetch && isLoading,
    isError: Boolean(error)
  };
};

export default useTestTabFilterMetadata;
